import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useChargePointsStore,
  type ChargePointsState,
} from '../store/charge-points-store';
import { chargePointsApi } from '../api/charge-points-api';
import { useAuthStore } from '@/features/auth/store/auth-store';
import { useGoogleMapsStore } from '@/features/map/store/google-maps-store';
import { useLocationStore } from '@/features/location/store/location-store';
import type { LatLng } from '@/types/global';
import type { ChargePointWrapper } from '../types/charge-point.types';

// A link without lat/lng (e.g. ?chargePointId=123) must still show the pin
function focusMapIfNoUrlLocation(coordinate: LatLng | undefined) {
  if (!coordinate || useLocationStore.getState().urlParams.lat !== undefined)
    return;
  const map = useGoogleMapsStore.getState().mapInstance;
  if (map) {
    map.panTo(coordinate);
    map.setZoom(15);
  } else {
    // Map not mounted yet: it reads these as its default center/zoom
    useLocationStore.setState({ mapCenter: coordinate, zoomLevel: 15 });
  }
}

/**
 * Hook to handle deep linking to charge points via URL params
 * Fetches and selects charge points when cpId or cpSiteId are in the URL
 *
 * No-op in charge-sessions mode (BIZOPS-1588): charge-point deep-linking is
 * irrelevant there and its fetches must not fire (a stray 401 would silently
 * re-mint a guest token). The effects below bail out before issuing any
 * request when `mapType=chargeSessions`.
 */
export function useDeepLinking() {
  const [searchParams] = useSearchParams();
  const isChargeSessionsMode = searchParams.get('mapType') === 'chargeSessions';
  // This hook mounts before the auth render gate, so wait for the guest token:
  // fetching earlier 401s and a shared link never opens for first-time visitors
  const isAuthReady = useAuthStore((s) => s.isInitialized);

  const urlChargePointId = useChargePointsStore(
    (s: ChargePointsState) => s.urlChargePointId
  );
  const urlChargePointSiteId = useChargePointsStore(
    (s: ChargePointsState) => s.urlChargePointSiteId
  );
  const selectChargePoint = useChargePointsStore(
    (s: ChargePointsState) => s.selectChargePoint
  );
  const setLoadingDetails = useChargePointsStore(
    (s: ChargePointsState) => s.setLoadingDetails
  );
  const setLoadingGroup = useChargePointsStore(
    (s: ChargePointsState) => s.setLoadingGroup
  );
  const setSelectedGroupMembers = useChargePointsStore(
    (s: ChargePointsState) => s.setSelectedGroupMembers
  );

  useEffect(() => {
    // Handle single charge point ID
    if (isAuthReady && !isChargeSessionsMode && urlChargePointId) {
      let isCancelled = false;

      const fetchChargePoint = async () => {
        setLoadingDetails(true);
        try {
          const data =
            await chargePointsApi.getChargePointById(urlChargePointId);

          // Only update state if user hasn't navigated away
          if (!isCancelled) {
            // Create a wrapper for the charge point
            const wrapper: ChargePointWrapper = {
              id: `cp-${data.id}`, // must match map marker ids so the pin highlights
              type: 'chargePoint',
              chargePoint: data,
              coordinate: data.coordinate,
              pin: data.pin,
            };

            selectChargePoint(wrapper);
            focusMapIfNoUrlLocation(data.coordinate);
          }
        } catch (error) {
          console.error('Failed to fetch charge point:', error);
        } finally {
          if (!isCancelled) {
            setLoadingDetails(false);
          }
        }
      };

      void fetchChargePoint();

      // Cleanup: cancel state updates if URL param changes or component unmounts
      return () => {
        isCancelled = true;
      };
    }
    return undefined;
  }, [
    isAuthReady,
    isChargeSessionsMode,
    urlChargePointId,
    selectChargePoint,
    setLoadingDetails,
  ]);

  useEffect(() => {
    // Handle charge point site/group ID
    if (isAuthReady && !isChargeSessionsMode && urlChargePointSiteId) {
      let isCancelled = false;

      const fetchChargePointGroup = async () => {
        setLoadingGroup(true);
        try {
          // Fetch the group data
          const groupData =
            await chargePointsApi.getChargePointSiteById(urlChargePointSiteId);

          // Fetch group members
          const members =
            await chargePointsApi.getChargePointGroupMembers(
              urlChargePointSiteId
            );

          // Only update state if user hasn't navigated away
          if (!isCancelled) {
            // Create a wrapper for the group
            const wrapper: ChargePointWrapper = {
              id: `group-${groupData.id}`,
              type: 'chargePointGroup',
              chargePointGroup: groupData,
              coordinate: groupData.coordinate,
              pin: groupData.pin,
            };

            setSelectedGroupMembers(members);
            selectChargePoint(wrapper);
            focusMapIfNoUrlLocation(groupData.coordinate);
          }
        } catch (error) {
          console.error(
            '[useDeepLinking] Failed to fetch charge point group:',
            error
          );
        } finally {
          if (!isCancelled) {
            setLoadingGroup(false);
          }
        }
      };

      void fetchChargePointGroup();

      // Cleanup: cancel state updates if URL param changes or component unmounts
      return () => {
        isCancelled = true;
      };
    }
    return undefined;
  }, [
    isAuthReady,
    isChargeSessionsMode,
    urlChargePointSiteId,
    selectChargePoint,
    setLoadingGroup,
    setSelectedGroupMembers,
  ]);
}
